import { useEffect, useMemo, useRef, useState } from 'react';
import { KPIMetric } from '../../types/dashboard';
import { KPICard } from './KPICard';
import { FinanceV2Section } from './FinanceV2Section';

// Matches the ``grid-template-columns`` ``minmax(min(100%, 18rem), 1fr)``
// floor. 18rem at the default 16px root = 288px. Anything below this
// collapses to one column (same as the CSS).
const KPI_GRID_MIN_CARD_PX = 288;

function useGridColumnCount(minCardWidthPx: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Start at 1 so SSR / first paint never produces fillers that would
  // duplicate cards. Real count lands on first ResizeObserver callback.
  const [columns, setColumns] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const recompute = () => {
      const w = el.getBoundingClientRect().width;
      const cols = Math.max(1, Math.floor(w / minCardWidthPx));
      setColumns(cols);
    };
    recompute();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recompute);
      return () => window.removeEventListener('resize', recompute);
    }
    const obs = new ResizeObserver(recompute);
    obs.observe(el);
    return () => obs.disconnect();
  }, [minCardWidthPx]);

  return { ref, columns };
}

interface KPIGridProps {
  metrics: KPIMetric[];
  onMetricSelect?: (metric: KPIMetric) => void;
  selectedMetric?: KPIMetric | null;
  /** Accepted for caller compatibility with the still-wired close
   *  callback; the retired ChartDrillDownSheet that used it is gone,
   *  so this is a no-op here. */
  onMetricClose?: () => void;
  /** Accepted for caller compatibility; not read. */
  botMessageId?: string | null;
  onShowLineage?: (metric: KPIMetric) => void;
  showSectionChrome?: boolean;
}

// The old ``ChartDrillDownSheet`` mount was removed as part of the
// single-schema cleanup — the Info icon on every KPICard now routes
// through ``onShowLineage`` to the unified LineagePanel (right-rail
// drawer driven by ``metric.lineage_v2``). The ``onMetricClose`` /
// ``botMessageId`` props are still accepted for caller compatibility but no
// longer drive an internal modal here.
export function KPIGrid({
  metrics,
  onMetricSelect,
  selectedMetric,
  onShowLineage,
  showSectionChrome = true,
}: KPIGridProps) {

  // Keep meaningful zero/dash KPI values; only skip missing or empty values.
  const validMetrics = useMemo(() => {
    return metrics.filter(metric => {
      if (metric.value === null || metric.value === undefined) return false;
      if (typeof metric.value === 'string' && metric.value.trim().length === 0) return false;
      return true;
    });
  }, [metrics]);

  const { ref: gridRef, columns } = useGridColumnCount(KPI_GRID_MIN_CARD_PX);

  // Don't render if no valid metrics
  if (validMetrics.length === 0) return null;

  const kpiCards = validMetrics.map((metric, index) => (
    <KPICard
      key={metric.id || `${metric.label}-${index}`}
      metric={metric}
      index={index}
      onClick={() => onMetricSelect?.(metric)}
      isSelected={selectedMetric?.id === metric.id}
      onShowLineage={onShowLineage}
    />
  ));

  // Trailing white filler cells. The grid uses ``bg-[#EDE5D8]`` +
  // ``gap-[1px]`` to draw the beige dividers between tiles, which
  // means any *empty* trailing cell in the final row shows that
  // beige through. Inject as many white-filled cells as the row
  // needs to look complete.
  const fillerCount = columns > 0
    ? (columns - (validMetrics.length % columns)) % columns
    : 0;
  const fillerCells = Array.from({ length: fillerCount }, (_, i) => (
    <div key={`kpi-filler-${i}`} aria-hidden="true" className="bg-white" />
  ));

  if (validMetrics.length === 1) {
    return (
      <div className="mx-auto w-full max-w-[640px] overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
        {kpiCards[0]}
      </div>
    );
  }

  if (showSectionChrome) {
    return (
      <FinanceV2Section
        title="Key numbers"
        icon={<span className="font-display text-[20px] leading-none text-[#67ADCA]">§</span>}
      >
        <div
          ref={gridRef}
          className="grid min-w-0 grid-cols-1 gap-[1px] overflow-hidden rounded-[10px] bg-[#EDE5D8]"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 18rem), 1fr))' }}
        >
          {kpiCards}
          {fillerCells}
        </div>
      </FinanceV2Section>
    );
  }

  return (
    <div
      ref={gridRef}
      className="grid min-w-0 grid-cols-1 gap-[1px] overflow-hidden rounded-[10px] bg-[#EDE5D8]"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 18rem), 1fr))' }}
    >
      {kpiCards}
      {fillerCells}
    </div>
  );
}
